import React from 'react';
import '../styles/components/MiniTimeline.css';
import { getSentimentIcon } from '../utils/iconMappings';

// Simplified SpeechSegment interface specifically for the timeline component
interface SpeechSegment {
  id: number;
  start_ms: number;
  end_ms: number;
  speaker_name: string;
  // Optional fields that might be needed for API data but not by this component
  transcript_id?: number;
  speech?: string;
  duration?: number;
}

interface MomentData {
  id: number;
  name: string;
  sentiment: string;
}

interface SpeechTextMoment {
  [key: string]: MomentData;
}

interface MiniTimelineProps {
  interactionId: number;
  timelineData: SpeechSegment[];
  mmts?: SpeechTextMoment;
}

const MiniTimeline: React.FC<MiniTimelineProps> = ({ interactionId, timelineData, mmts = {} }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  console.log('MiniTimeline mmts:', mmts);
  console.log('MiniTimeline timelineData:', timelineData);
      // console.log(`MiniTimeline for interaction ${interactionId} received:`, timelineData);
  
  if (!timelineData || timelineData.length === 0) {
    // console.log(`MiniTimeline for interaction ${interactionId}: No timeline data`);
    return <div className="mini-timeline-empty">No timeline data</div>;
  }

  console.log('MiniTimeline render:', {
    interactionId,
    timelineDataLength: timelineData.length,
    mmtsLength: Object.keys(mmts).length,
    mmts
  });

  // Get total call duration from the last end_ms
  const maxEndTime = Math.max(...timelineData.map(segment => segment.end_ms));
  
  // Filter segments by speaker
  const agentSegments = timelineData.filter(segment => segment.speaker_name === "Agent");
  const customerSegments = timelineData.filter(segment => segment.speaker_name === "Customer");
  
  // Calculate silence segments (gaps between any speech)
  const allSegments = [...timelineData];
  allSegments.sort((a, b) => a.start_ms - b.start_ms);
  
  const silenceSegments: SpeechSegment[] = [];
  let lastEndTime = 0;
  
  allSegments.forEach(segment => {
    if (segment.start_ms > lastEndTime) {
      silenceSegments.push({
        id: -silenceSegments.length - 1, // Negative IDs for silence segments
        start_ms: lastEndTime,
        end_ms: segment.start_ms,
        speaker_name: "Silence",
        transcript_id: -1, // Add required properties with placeholder values
        speech: "", 
        duration: (segment.start_ms - lastEndTime) / 1000
      });
    }
    lastEndTime = Math.max(lastEndTime, segment.end_ms);
  });

  // Add a final silence segment if needed
  if (lastEndTime < maxEndTime) {
    silenceSegments.push({
      id: -silenceSegments.length - 1,
      start_ms: lastEndTime,
      end_ms: maxEndTime,
      speaker_name: "Silence",
      transcript_id: -1,
      speech: "",
      duration: (maxEndTime - lastEndTime) / 1000
    });
  }

  const renderSpeechSegments = (segments: SpeechSegment[], className: string) => {
    return segments.map((segment) => {
      const left = (segment.start_ms / maxEndTime) * 100;
      const width = ((segment.end_ms - segment.start_ms) / maxEndTime) * 100;
      
      return (
        <div
          key={segment.id}
          className={`speech-segment ${className}`}
          style={{
            left: `${left}%`,
            width: `${width}%`,
            position: 'absolute'
          }}
          title={`${(segment.start_ms / 1000).toFixed(1)}s:${segment.speech}`}
        />
      );
    });
  };

  return (
    <div 
      className={`mini-timeline ${isExpanded ? 'expanded' : ''}`}
      onClick={(e) => {
        e.stopPropagation();  // Stop event from bubbling up to parent
        setIsExpanded(!isExpanded);
      }}>
      {/* Time indicators and moments */}
      <div className="timeline-header">
        <div className="timeline-markers">
          <div className="timeline-marker start">0s</div>
          <div className="timeline-marker middle">{(maxEndTime / 2000).toFixed(0)}s</div>
          <div className="timeline-marker end">{(maxEndTime / 1000).toFixed(0)}s</div>
        </div>
        
        {/* Moment indicators */}
        <div className="timeline-moments">
          {Object.entries(mmts).map(([speechId, moment]) => {
            console.log('Processing moment:', { speechId, moment });
            const speechSegment = timelineData.find(segment => segment.id === parseInt(speechId));
            console.log('Found speech segment:', speechSegment);
            if (!speechSegment) return null;
            const left = (speechSegment.start_ms / maxEndTime) * 100;
            console.log('Calculated position:', { left, start_ms: speechSegment.start_ms, maxEndTime });
            return (
              <div
                key={`${speechId}-${moment.id}`}
                className="moment-indicator"
                style={{ left: `${left}%` }}
                title={moment.name}
              >
                <span className="material-symbols-outlined">{getSentimentIcon(moment.sentiment)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Agent speech line */}
      <div className="timeline-row agent-line">
        {renderSpeechSegments(agentSegments, 'agent-segment')}
      </div>
      
      {/* Silence line */}
      <div className="timeline-row silence-line">
        {renderSpeechSegments(silenceSegments, 'silence-segment')}
      </div>
      
      {/* Customer speech line */}
      <div className="timeline-row customer-line">
        {renderSpeechSegments(customerSegments, 'customer-segment')}
      </div>
      
      {/* Dot indicators for key events */}
      {/* <div className="timeline-indicators">
        <div className="timeline-indicator" style={{ left: '0%' }}></div>
        <div className="timeline-indicator" style={{ left: '50%' }}></div>
        <div className="timeline-indicator" style={{ left: '100%' }}></div>
      </div> */}

    </div>
  );
};

export default MiniTimeline; 